<template>
	<div id="app">
		<div class="module-pages module-pages--letbane">
			<template v-if="bootstrapped">
				<letbane-header @openHelp="openHelpModal()" />

				<router-view v-show="!loading" :key="$route.fullPath" @loading="onLoading" />
			</template>
		</div>
		<slot />

		<modal-letbane-help :name="helpLetbane" />
	</div>
</template>

<script>
import './assets/theme.scss'
import { loadLanguageAsync } from '@/i18n'

const LetbaneHeader = () => import(/* webpackMode: "eager", webpackChunkName: "letbane-header" */ './components/blocks/LetbaneHeader')
const ModalLetbaneHelp = () =>
	import(/* webpackMode: "lazy", webpackChunkName: "modal-letbane-help" */ '@/components/modals/ModalLetbaneHelp')

export default {
	name: 'Fynbus',
	components: {
		LetbaneHeader,
		ModalLetbaneHelp
	},
	props: {
		loading: Boolean,
		loadingMessage: { type: String, required: false, default: '' },
		onLoading: { type: Function, default: () => {} },
		bootstrapped: { type: Boolean, required: true }
	},
	data() {
		return {
			isNembilletSalesChannel: false,
			availableLanguages: JSON.parse(process.env.VUE_APP_AVAILABLE_LANGUAGES)
		}
	},
	computed: {
		helpLetbane: function() {
			switch (this.$i18n.locale) {
				case 'da':
				case 'en':
					return 'HelpOdenseTram'
				default:
					loadLanguageAsync(this.availableLanguages[0])
					return 'HelpOdenseTram'
			}
		}
	},
	watch: {
		$route() {
			const params = new URL(window.location).searchParams
			const saleschannel = params.get('salesChannel') ? params.get('salesChannel') : null
			if (saleschannel) {
				this.isNembilletSalesChannel = saleschannel === 'Nembillet'
			}
		}
	},
	created() {
		const params = new URL(window.location).searchParams
		const saleschannel = params.get('salesChannel') ? params.get('salesChannel') : null
		if (saleschannel) {
			this.isNembilletSalesChannel = saleschannel === 'Nembillet'
		}
		let link = document.querySelector("link[rel~='icon']")

		if (this.isNembilletSalesChannel) {
			document.title = 'Odense Letbane'
			if (!link) {
				link = document.createElement('link')
				link.rel = 'icon'
				document.head.appendChild(link)
			}
			link.href = '/static/images/fynbus.ico'
			document.querySelector('meta[name="theme-color"]').setAttribute('content', '#82b71b')
		} else {
			document.title = 'Odense Letbane - køb billet'
			if (!link) {
				link = document.createElement('link')
				link.rel = 'icon'
				document.head.appendChild(link)
			}
			link.href = '/static/images/letbane.png'
			document.querySelector('meta[name="theme-color"]').setAttribute('content', '#F7F7F7')
		}
	},
	mounted() {},
	methods: {
		openHelpModal() {
			this.$modal.show('letbane-help-modal')
		}
	}
}
</script>

<style scoped lang="scss">
.privacy-policy {
	position: fixed;
	bottom: 20px;
	right: 10px;
	z-index: 999;
}

.loading {
	margin: auto;
	height: 3em;
	font-weight: bold;
	margin-top: 1em;
	text-align: center;
}

.module-pages {
	max-width: 75rem;
	margin: 0 auto;
	padding: 2em 0.5em;

	&--letbane {
		max-width: 24.5625rem;
		margin: 0 auto;
		min-height: 100%;
		padding: 2rem 1.5rem;
		box-sizing: border-box;
		display: flex;
		flex-direction: column;
		flex: 1;

		& .not-supported {
			font-size: 1.625rem;
			font-weight: 400;
			line-height: 2rem;
			flex: 1;
			display: flex;
			align-items: center;
			margin: 0;
			text-align: center;
		}
	}
}

@media all and (max-width: 393px) {
	.module-pages {
		max-width: 98% !important;
		margin: 0 auto;

		&--letbane {
			max-width: 100% !important;
		}
	}
}
</style>

<style lang="scss">
#app:has(.module-pages--letbane) {
	position: relative;
	min-height: 100vh;
	display: flex;
}

// hiding cookiebot for letbane
#CybotCookiebotDialog:has(+ #app > .module-pages--letbane) {
	display: none !important;
}

// styling for phone countrycode
.vue-country-select {
	background-color: $letbane-color-white;
	border-top-left-radius: 0.25rem !important;
	border-bottom-left-radius: 0.25rem !important;
	border-top-right-radius: 0 !important;
	border-bottom-right-radius: 0 !important;
	border: 0.0625rem solid $letbane-color-stroke !important;
	border-right: none !important;
	height: 100% !important;
	box-shadow: none !important;
	box-sizing: border-box;

	&--error {
		border: 0.0625rem solid red !important;
		border-right: none !important;
	}

	&:has(.dropdown.open) {
		& ~ .page__phone-input {
			border-bottom-right-radius: 0 !important;
		}
	}

	& .dropdown {
		padding: 0 !important;
		padding-left: 0.875rem !important;
		padding-right: 0.875rem !important;

		&:hover {
			background-color: $letbane-color-white !important;
			border-top-left-radius: 0.25rem !important;
			border-bottom-left-radius: 0.25rem !important;
		}

		&.open {
			background-color: $letbane-color-white !important;
			border-top-left-radius: 0.25rem !important;
			border-bottom-left-radius: 0 !important;
		}

		.current {
			column-gap: 0.25rem;
		}
	}

	& .vti__flag {
		border-radius: 0.25rem;
	}

	& .dropdown-list {
		width: calc(100vw - 3.125rem) !important;
		max-width: 21.4375rem !important;
		border: 0.0625rem solid $letbane-color-stroke !important;
		overflow-y: auto !important;
		border-bottom-left-radius: 0.25rem !important;
		border-bottom-right-radius: 0.25rem !important;
	}

	& .dropdown-item {
		font-family: $letbane-font-family;
		display: grid;
		align-items: center;
		grid-template-columns: 1.875rem 1fr auto;
		column-gap: 0.625rem;

		strong {
			font-weight: 300 !important;
			font-size: 0.875rem !important;
		}

		span {
			font-weight: 300 !important;
			font-size: 0.875rem !important;
		}
	}
}
</style>
